<template>
  <Teleport to="body">
    <transition name="image-fullscreen-fade">
      <div
        v-if="modelValue && activeItem"
        class="fixed inset-0 z-[1600] flex flex-col bg-black/88 px-3 py-4 sm:p-6"
        @click="close"
      >
        <div
          class="absolute right-4 top-4 z-[2] flex items-center gap-2 sm:right-6 sm:top-6"
        >
          <button
            type="button"
            class="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/18 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:cursor-wait disabled:opacity-60"
            :aria-label="isDownloading ? 'Скачивание файла' : 'Скачать файл'"
            :title="isDownloading ? 'Скачивание файла' : 'Скачать файл'"
            :disabled="isDownloading"
            @click.stop="downloadActiveMedia"
          >
            <span
              v-if="isDownloading"
              class="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white"
              aria-hidden="true"
            ></span>
            <svg
              v-else
              viewBox="0 0 24 24"
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M12 3v12" stroke-linecap="round" />
              <path
                d="m7 10 5 5 5-5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path d="M5 21h14" stroke-linecap="round" />
            </svg>
          </button>
          <button
            type="button"
            class="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/18 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            aria-label="Закрыть просмотр изображения"
            title="Закрыть"
            @click.stop="close"
          >
            <span
              class="absolute h-[2px] w-5 rotate-45 rounded-full bg-current"
            ></span>
            <span
              class="absolute h-[2px] w-5 -rotate-45 rounded-full bg-current"
            ></span>
          </button>
        </div>

        <p
          v-if="downloadError"
          class="absolute right-4 top-[68px] z-[2] max-w-[min(280px,calc(100vw-32px))] text-right text-[12px] text-orange sm:right-6 sm:top-[76px]"
          role="status"
        >
          {{ downloadError }}
        </p>

        <div
          v-if="mediaItems.length > 1"
          class="absolute left-1/2 top-4 z-[1] -translate-x-1/2 rounded-full border border-white/15 bg-black/35 px-3 py-1 text-[12px] font-helvetica-700 text-white sm:top-6"
        >
          {{ activeIndex + 1 }} / {{ mediaItems.length }}
        </div>

        <div
          class="flex min-h-0 w-full flex-1 items-center justify-center pb-3 pt-12 sm:pt-8"
        >
          <div
            class="image-fullscreen-surface flex items-center justify-center rounded-[14px] bg-white p-3 shadow-[0_20px_60px_rgb(0_0_0_/_0.25)] sm:p-4"
            :class="[
              mediaItems.length > 1
                ? 'image-fullscreen-surface--with-thumbs'
                : 'image-fullscreen-surface--single',
              activeItem.type === 'pdf' ? 'image-fullscreen-surface--pdf' : '',
              rectangularImageSize
                ? 'image-fullscreen-surface--rectangular'
                : '',
            ]"
            :style="rectangularImageSize || undefined"
            @click.stop
          >
            <VideoPlayer
              v-if="activeItem.type === 'video'"
              class="h-full w-full overflow-hidden rounded-[10px]"
              :video-src="activeItem.src"
            />
            <PdfPreview
              v-else-if="activeItem.type === 'pdf'"
              :src="activeItem.src"
              :show-download-button="false"
            />
            <img
              v-else
              :src="activeItem.src"
              :alt="alt"
              class="max-h-full max-w-full rounded-[10px] object-contain"
              @load="captureImageAspectRatio"
            />
          </div>
        </div>

        <div
          v-if="mediaItems.length > 1"
          class="w-full shrink-0 pb-[env(safe-area-inset-bottom)]"
          @click.stop
        >
          <div
            class="mx-auto flex max-w-full gap-2 overflow-x-auto px-1 py-2 sm:justify-center"
          >
            <button
              v-for="(item, index) in mediaItems"
              :key="`${item.src}-${index}`"
              type="button"
              class="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-[8px] border bg-white p-1 transition-colors sm:h-16 sm:w-16"
              :class="
                index === activeIndex
                  ? 'border-orange cursor-default'
                  : 'border-white/30 cursor-pointer hover:border-white'
              "
              :aria-pressed="index === activeIndex"
              :aria-label="`Открыть медиа ${index + 1}`"
              @click="selectMedia(index)"
            >
              <img
                v-if="item.type === 'image'"
                :src="item.src"
                alt=""
                class="h-full w-full object-contain"
              />
              <div
                v-else-if="item.type === 'video'"
                class="flex h-full w-full items-center justify-center rounded-[4px] bg-gray-muted"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  class="text-white-card"
                >
                  <path d="M5 3.5v9l7-4.5-7-4.5z" />
                </svg>
              </div>
              <div
                v-else
                class="h-full w-full overflow-hidden rounded-[4px] bg-white"
              >
                <PdfPreview :src="item.src" compact :show-open-button="false" />
                <svg
                  viewBox="0 0 16 16"
                  class="hidden h-6 w-6 text-orange"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM1.6 11.85H0v3.999h.791v-1.342h.803q.43 0 .732-.173.305-.175.463-.474a1.4 1.4 0 0 0 .161-.677q0-.375-.158-.677a1.2 1.2 0 0 0-.46-.477q-.3-.18-.732-.179m.545 1.333a.8.8 0 0 1-.085.38.57.57 0 0 1-.238.241.8.8 0 0 1-.375.082H.788V12.48h.66q.327 0 .512.181.185.183.185.522m1.217-1.333v3.999h1.46q.602 0 .998-.237a1.45 1.45 0 0 0 .595-.689q.196-.45.196-1.084 0-.63-.196-1.075a1.43 1.43 0 0 0-.589-.68q-.396-.234-1.005-.234zm.791.645h.563q.371 0 .609.152a.9.9 0 0 1 .354.454q.118.302.118.753a2.3 2.3 0 0 1-.068.592 1.1 1.1 0 0 1-.196.422.8.8 0 0 1-.334.252 1.3 1.3 0 0 1-.483.082h-.563zm3.743 1.763v1.591h-.79V11.85h2.548v.653H7.896v1.117h1.606v.638z"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useMediaDownload } from "~/composables/useMediaDownload";
import PdfPreview from "~/components/PdfPreview.vue";
import VideoPlayer from "~/components/VideoPlayer.vue";
import {
  getFullscreenImageSize,
  isRectangularImage,
} from "~/utils/fullscreenMedia";
import { normalizeMediaUrl } from "~/utils/normalizeMediaUrl";

type MediaType = "image" | "video" | "pdf";
type MediaItem = { src: string; type: MediaType };

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    imageSrc: string;
    alt?: string;
    mediaSrcs?: string[];
    selectedIndex?: number;
  }>(),
  {
    alt: "Изображение",
    mediaSrcs: () => [],
    selectedIndex: 0,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "update:selectedIndex", value: number): void;
}>();

const videoExtensions = new Set(["mp4", "webm", "mov", "m4v", "ogg"]);
const pdfExtensions = new Set(["pdf"]);

const getExtension = (src: string) => {
  const clean = (src.split("?")[0] ?? "").trim().toLowerCase();
  const dotIndex = clean.lastIndexOf(".");
  if (dotIndex === -1) return "";
  return clean.slice(dotIndex + 1);
};

const getMediaType = (src: string): MediaType =>
  videoExtensions.has(getExtension(src))
    ? "video"
    : pdfExtensions.has(getExtension(src))
      ? "pdf"
      : "image";

const mediaItems = computed<MediaItem[]>(() => {
  const seen = new Set<string>();
  return [...props.mediaSrcs, props.imageSrc]
    .map((src) => normalizeMediaUrl(src))
    .filter((src): src is string => {
      if (!src || seen.has(src)) return false;
      seen.add(src);
      return true;
    })
    .map((src) => ({
      src,
      type: getMediaType(src),
    }));
});

const activeIndex = computed(() => {
  if (!mediaItems.value.length) return 0;
  if (props.selectedIndex < 0) return 0;
  if (props.selectedIndex >= mediaItems.value.length) return 0;
  return props.selectedIndex;
});

const activeItem = computed(() => mediaItems.value[activeIndex.value] ?? null);
const { downloadError, downloadMedia, isDownloading } = useMediaDownload();
const imageAspectRatios = ref<Record<string, number>>({});
const viewport = ref({ width: 0, height: 0 });

const activeImageAspectRatio = computed(() => {
  if (activeItem.value?.type !== "image") return 0;
  return imageAspectRatios.value[activeItem.value.src] ?? 0;
});

const rectangularImageSize = computed(() => {
  if (!isRectangularImage(activeImageAspectRatio.value)) return null;

  return getFullscreenImageSize({
    aspectRatio: activeImageAspectRatio.value,
    hasThumbnails: mediaItems.value.length > 1,
    viewportWidth: viewport.value.width,
    viewportHeight: viewport.value.height,
  });
});

const captureImageAspectRatio = (event: Event) => {
  const image = event.target as HTMLImageElement;
  const source = image.getAttribute("src") || image.currentSrc;
  if (!source || !image.naturalWidth || !image.naturalHeight) return;

  imageAspectRatios.value = {
    ...imageAspectRatios.value,
    [source]: image.naturalWidth / image.naturalHeight,
  };
};

const updateViewport = () => {
  if (typeof window === "undefined") return;
  viewport.value = { width: window.innerWidth, height: window.innerHeight };
};

const downloadActiveMedia = () => {
  if (!activeItem.value) return;
  void downloadMedia(activeItem.value.src);
};

const close = () => {
  emit("update:modelValue", false);
};

const selectMedia = (index: number) => {
  if (index < 0 || index >= mediaItems.value.length) return;
  emit("update:selectedIndex", index);
};

const showPrevMedia = () => {
  if (mediaItems.value.length <= 1) return;
  selectMedia(
    (activeIndex.value - 1 + mediaItems.value.length) % mediaItems.value.length,
  );
};

const showNextMedia = () => {
  if (mediaItems.value.length <= 1) return;
  selectMedia((activeIndex.value + 1) % mediaItems.value.length);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.modelValue) return;
  if (event.key === "Escape") {
    close();
    return;
  }

  if (event.key === "ArrowLeft") {
    event.preventDefault();
    showPrevMedia();
    return;
  }

  if (event.key === "ArrowRight") {
    event.preventDefault();
    showNextMedia();
  }
};

watch(
  () => props.modelValue,
  (isOpen) => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = isOpen ? "hidden" : "";
  },
  { immediate: true },
);

onMounted(() => {
  updateViewport();
  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("resize", updateViewport);
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("keydown", handleKeydown);
    window.removeEventListener("resize", updateViewport);
  }
  if (typeof document !== "undefined") {
    document.body.style.overflow = "";
  }
});
</script>

<style scoped>
.image-fullscreen-fade-enter-active,
.image-fullscreen-fade-leave-active {
  transition: opacity 0.2s ease;
}

.image-fullscreen-fade-enter-from,
.image-fullscreen-fade-leave-to {
  opacity: 0;
}

.image-fullscreen-surface {
  aspect-ratio: 1 / 1;
  max-width: calc(100vw - 24px);
  max-height: calc(100dvh - 112px);
}

.image-fullscreen-surface--single {
  width: min(82vw, calc(100dvh - 112px));
}

.image-fullscreen-surface--with-thumbs {
  width: min(82vw, calc(100dvh - 190px));
  max-height: calc(100dvh - 190px);
}

.image-fullscreen-surface--pdf {
  aspect-ratio: auto;
  width: min(92vw, 1040px);
  height: min(76dvh, 780px);
  max-height: calc(100dvh - 190px);
}

.image-fullscreen-surface--pdf.image-fullscreen-surface--single {
  height: min(82dvh, 840px);
  max-height: calc(100dvh - 112px);
}

.image-fullscreen-surface--rectangular {
  aspect-ratio: auto;
  max-width: calc(100vw - 24px);
}

@media (min-width: 640px) {
  .image-fullscreen-surface {
    max-width: 880px;
    max-height: calc(100dvh - 96px);
  }

  .image-fullscreen-surface--rectangular {
    max-width: calc(100vw - 48px);
  }

  .image-fullscreen-surface--single {
    width: min(76vw, calc(100dvh - 96px));
  }

  .image-fullscreen-surface--with-thumbs {
    width: min(76vw, calc(100dvh - 184px));
    max-height: calc(100dvh - 184px);
  }

  .image-fullscreen-surface--pdf {
    width: min(86vw, 1040px);
    height: min(76dvh, 820px);
    max-width: 1040px;
    max-height: calc(100dvh - 184px);
  }

  .image-fullscreen-surface--pdf.image-fullscreen-surface--single {
    height: min(82dvh, 880px);
    max-height: calc(100dvh - 96px);
  }
}
</style>
